import { Grid } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { groupBy, orderBy } from 'lodash'
import CommercialFeatureExplorer from './CommercialFeatureExplorer'
import { hasPermission } from 'common/utils/HabilitationUtil'
import useBoolean from 'common/hooks/useBoolean'
import ConfirmModal from 'common/components/modal/ConfirmModal'
import DialogCommercialFeature from './DialogCommercialFeature'
import useActions from 'common/hooks/useActions'
import { NEW } from 'common/components/action/ActionConstant'
import useTitles from 'common/hooks/useTitles'
import {
    deleteCommercialFeature,
    fetchAdminFeatureLinks,
    fetchCommercialFeatures,
    selectCommercialFeatures,
} from 'gdl/commercial/commercialFeature/CommercialFeaturesSlice'

const CommercialFeatureApp = () => {
    const dispatch = useDispatch()
    const {
        user,
        commercialFeatures,
    } = useSelector(store => ({
        user: store.app.user,
        commercialFeatures: store.gdl.commercialFeatures,
    }), shallowEqual)
    const listCommercialFeatures = useSelector(selectCommercialFeatures, shallowEqual)
    const [adminFeatureLinks, setAdminFeatureLinks] = useState([])

    useTitles(() => [{
        label: 'Commerce',
        href: '/commercial',
    }, {
        label: 'Catalogue',
        href: '/commercial/feature',
    }], [])

    useEffect(() => {
        dispatch(fetchAdminFeatureLinks()).then(action => setAdminFeatureLinks(action.payload))
    }, [dispatch])

    const [selectedCommercialFeature, setSelectedCommercialFeature] = useState({})
    const {
        value: isDialogOpen,
        setTrue: onOpenDialog,
        setFalse: onCloseDialog,
    } = useBoolean(false)
    const {
        value: isConfirmOpen,
        setTrue: onOpenConfirm,
        setFalse: onCloseConfirm,
    } = useBoolean(false)

    useActions(() => [{
        type: NEW,
        onClick: onOpenDialog,
        displayed: hasPermission(user, 'commercial', 'create'),
    }], [])

    const formattedGroupedCommercialFeatures = useMemo(() => {
        const orderedCommercialFeatures = orderBy(listCommercialFeatures, ['typeFeature', 'code'], ['desc', 'asc'])
        const groupedCommercialFeatures = groupBy(orderedCommercialFeatures, s => s.parent ?? 'headTree')

        const formatCommercialFeatures = (filteredCommercialFeatures = []) => {
            return filteredCommercialFeatures.map(({ id, name, description, price }) => {
                const subFeatures = formatCommercialFeatures(groupedCommercialFeatures[id])
                return {
                    id,
                    label: name,
                    price,
                    description,
                    subFeatures,
                    nbFeatures: adminFeatureLinks.filter(link => link.idFeatureCommercial === id).length,
                }
            })
        }

        return formatCommercialFeatures(groupedCommercialFeatures.headTree)
    }, [listCommercialFeatures])

    const onCreate = id => {
        setSelectedCommercialFeature({ parent: id })
        onOpenDialog()
    }
    const onUpdate = id => {
        setSelectedCommercialFeature(commercialFeatures.byId[id])
        onOpenDialog()
    }
    const onDelete = id => {
        setSelectedCommercialFeature({ id })
        onOpenConfirm()
    }

    return (
        <>
            <Grid container spacing={1} alignItems='flex-start' sx={{ padding: '5px 15%' }}>
                {formattedGroupedCommercialFeatures.map(commercialFeature => (
                    <Grid key={commercialFeature.code} size={12}>
                        <CommercialFeatureExplorer
                            commercialFeature={commercialFeature}
                            actions={[
                                {
                                    icon: 'add_circle_outline',
                                    onClick: onCreate,
                                    displayed: hasPermission(user, 'commercial', 'create'),
                                },
                                {
                                    icon: 'edit',
                                    onClick: onUpdate,
                                    displayed: hasPermission(user, 'commercial', 'update'),
                                },
                                {
                                    icon: 'delete_outlined',
                                    onClick: onDelete,
                                    displayed: hasPermission(user, 'commercial', 'delete'),
                                },
                            ]}
                        />
                    </Grid>
                ))}
            </Grid>
            <DialogCommercialFeature
                isOpen={isDialogOpen}
                onClose={() => {
                    setSelectedCommercialFeature()
                    onCloseDialog()
                }}
                onValidate={() => dispatch(fetchCommercialFeatures())}
                commercialFeature={selectedCommercialFeature}
            />
            <ConfirmModal
                isOpen={isConfirmOpen}
                title='Êtes-vous sûr de vouloir supprimer cette fonctionnalité ?'
                onValidate={() => {
                    dispatch(deleteCommercialFeature(selectedCommercialFeature.id)).then(() => {
                        dispatch(fetchCommercialFeatures())
                        setSelectedCommercialFeature()
                        onCloseConfirm()
                    })
                }}
                onClose={onCloseConfirm}
            />
        </>
    )
}

export default CommercialFeatureApp